import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Leaf, Sun, Wind, CloudRain, Flower2, Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, ArrowRight,
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, TreePine, Bird, Mountain, Camera
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const NatureCampus: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Leaf;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Leaf;
  };

  const schoolName = previewData?.name || "Greenwood Valley";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1596495577886-d920f12d7d57?q=80&w=2070&auto=format&fit=crop"],
      tagline: "Cultivating Wisdom + Respect for Earth",
      titlePrefix: "Growth Happens",
      subtitle: "A sanctuary for learning where students connect with nature, themselves, and the community. We prioritize holistic development and ecological stewardship."
    },
    features: [
      { icon: "Leaf", title: "Forest Learning", desc: "Academics integrated with outdoor exploration and self-discovery." },
      { icon: "Sun", title: "Solar Powered", desc: "Our campus serves as a living laboratory for sustainable living." },
      { icon: "CloudRain", title: "Water Cycle Labs", desc: "Understanding the fundamental systems of our planet's survival." }
    ],
    programs: [
      { title: "Roots Foundation", age: "Toddler - Kindergarten", desc: "Sensory-rich environments designed to spark innate curiosity and wonder.", img: "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=80&w=2038&auto=format&fit=crop" },
      { title: "Canopy Scholars", age: "Primary - Junior High", desc: "Project-based learning centered on environmental justice and standard excellence.", img: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=2071&auto=format&fit=crop" }
    ],
    stats: [
        { value: "40", label: "Heritage Trees" },
        { value: "100%", label: "Organic Meals" },
        { value: "Zero", label: "Waste Target" }
    ],
    news: [
        { title: "Winter Solstice Fair", date: "DEC 21", desc: "Celebrating the cycle of seasons with our local artisan community.", img: "https://images.unsplash.com/photo-1513271922711-58b227b1b4b2?q=80&w=2070&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'Philosophy', href: '#about' },
        { label: 'Growth Paths', href: '#programs' },
        { label: 'The Campus', href: '#facilities' },
        { label: 'Community', href: '#team' }
    ],
    team: [
        { name: "Saffron Green", role: "Head of Sanctuary", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1961&auto=format&fit=crop" },
        { name: "John Weaver", role: "Outdoor Educator", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Garden", img: "https://images.unsplash.com/photo-1592150621344-82d2188daacc?q=80&w=2070&auto=format&fit=crop", title: "Morning Harvest" },
        { category: "Art", img: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=2071&auto=format&fit=crop", title: "Natural Pigments" }
    ],
    facilities: [
        { title: "Living Lab", desc: "Hydroponic systems and weather stations.", img: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?q=80&w=2070&auto=format&fit=crop", icon: "Leaf" },
        { title: "The Treehouse", desc: "Elevated quiet space for literature and reflection.", img: "https://images.unsplash.com/photo-1448375240586-882707db888b?q=80&w=2070&auto=format&fit=crop", icon: "Bird" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "Naturally Here.",
          description: previewData?.about?.description || "At " + schoolName + ", we believe the Earth is our ultimate teacher. We don't just study Biology; we live it. We don't just read about community; we grow one, seed by seed.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1544928147-79a2e7c38568?q=80&w=2070&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "School_Eco_Charter.pdf", size: "2.1 MB", type: "PDF" },
          { title: "Outdoor_Equipment_List.pdf", size: "0.4 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "Sage Valley Road, Karen, Nairobi",
        phone: previewData?.contact?.phone || "+254 700 888 999",
        email: previewData?.contact?.email || "hello@greenwood.edu",
        officeHours: previewData?.contact?.officeHours || "Mon - Fri: 8:00 AM - 4:00 PM",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Eco Resources",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Garden Knowledge Base",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Start a Conversation",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Reach out to our eco-learning team for admissions, tours, or general stewardship inquiries."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#5D875F');
        root.style.setProperty('--color-secondary', colors.secondary || '#8C7851');
        root.style.setProperty('--color-accent', colors.accent || '#E6B325');
        root.style.setProperty('--color-background', colors.background || '#F9FAF7');
        root.style.setProperty('--color-text', colors.text || '#2C3E2D');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Lora'}', serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Outfit'}', sans-serif`);
        const radiusMap = { none: '0px', small: '8px', medium: '24px', large: '48px', full: '9999px' };
        root.style.setProperty('--radius', radiusMap[previewData.theme.borderRadius || 'medium']);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,700;1,400&family=Outfit:wght@300;400;500;700&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Return this item to the earth?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="nature-campus bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      
      {/* Organic Header */}
      <nav className="fixed top-0 w-full z-50 p-6 lg:p-8 flex justify-between items-center transition-all duration-300">
          <div className="absolute inset-0 bg-white/40 backdrop-blur-md -z-10" />
          <div className="flex items-center gap-4">
               <VisualImageEditable 
                  src={previewData?.logoUrl || ""} 
                  alt="Logo" 
                  containerClassName="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm p-1"
                  className="w-full h-full object-contain"
                  onImageChange={(u) => handleUpdate('logoUrl', u)}
                  isEditMode={!!isEditMode}
                  fallback={<Leaf size={24} className="text-[var(--color-primary)]" />}
              />
              <div className="font-bold text-2xl tracking-tight text-[var(--color-text)] font-[var(--font-heading)]">
                  <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
              </div>
          </div>
          
          <div className="hidden lg:flex items-center gap-12">
              <div className="flex gap-10 text-[13px] font-semibold text-[var(--color-primary)]">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} className="hover:text-[var(--color-text)] transition-colors relative group">
                          {n.label}
                          <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[var(--color-accent)] group-hover:w-full transition-all duration-300" />
                      </a>
                  ))}
              </div>
              <div className="relative">
                  <button onClick={() => setShowLoginDropdown(!showLoginDropdown)} className="bg-[var(--color-text)] text-white px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-[var(--color-primary)] transition-all flex items-center gap-2">
                      Portals <ChevronDown size={14} className={showLoginDropdown ? 'rotate-180' : ''} />
                  </button>
                  {showLoginDropdown && (
                      <div className="absolute top-full right-0 mt-3 w-48 bg-white/95 backdrop-blur-xl p-2 z-50 shadow-2xl rounded-2xl border border-white/20">
                          <a href="https://sonoma.school/guardian-login" className="block p-4 text-xs font-semibold text-[var(--color-text)] hover:bg-[var(--color-background)] rounded-xl transition-all">
                              Parent Portal
                          </a>
                          <a href="https://sonoma.school/student-login" className="block p-4 text-xs font-semibold text-[var(--color-text)] hover:bg-[var(--color-background)] rounded-xl transition-all">
                              Student Village
                          </a>
                      </div>
                  )}
              </div>
          </div>

          <button className="lg:hidden text-[var(--color-primary)]" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
      </nav>

      {/* Hero Section - Airy & Botanical */}
      {mockData.sectionVisibility.hero && (
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-32 pb-20 items-center text-center">
          <div className="absolute top-20 right-20 w-80 h-80 bg-[var(--color-primary)]/5 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-[var(--color-secondary)]/5 rounded-full blur-3xl -z-10" />
          
          <div className="container mx-auto relative z-10 max-w-5xl">
              <span className="inline-block text-[var(--color-primary)] text-sm font-bold tracking-[0.3em] uppercase mb-8 border-b-2 border-[var(--color-accent)] pb-2">
                  <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
              </span>
              <h1 className="text-6xl md:text-8xl lg:text-[7rem] font-bold font-[var(--font-heading)] leading-[1.1] mb-12 text-[var(--color-text)]">
                  <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                  <span className="text-[var(--color-primary)] italic font-normal">Naturally Here.</span>
              </h1>
              <p className="max-w-2xl mx-auto text-xl text-[var(--color-primary)]/70 mb-16 leading-relaxed font-light">
                  <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
              </p>
              
              <div className="relative w-full aspect-video md:aspect-[21/9] rounded-[var(--radius)] overflow-hidden shadow-2xl">
                  <VisualImageEditable 
                      src={mockData.hero.backgroundImage} 
                      className="w-full h-full object-cover rounded-[var(--radius)]" 
                      alt="Nature Campus"
                      onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                      isEditMode={!!isEditMode}
                  />
                  <div className="absolute top-8 left-8 hidden md:block group">
                      <div className="bg-white/40 backdrop-blur-md rounded-full w-14 h-14 flex items-center justify-center text-[var(--color-text)] group-hover:bg-white transition-all cursor-pointer">
                          <Bird size={24} className="group-hover:animate-bounce" />
                      </div>
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Stats - Earthy Blocks */}
      {mockData.sectionVisibility.stats && (
      <section className="bg-white py-24 border-y border-[var(--color-primary)]/5">
          <div className="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-16 text-center">
              {mockData.stats.map((stat, i) => (
                  <div key={i} className="flex flex-col items-center">
                      <div className="text-6xl font-bold font-[var(--font-heading)] text-[var(--color-text)] mb-3">
                        <VisualTextEditable value={stat.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-sm font-semibold uppercase tracking-widest text-[var(--color-primary)] opacity-60">
                        <VisualTextEditable value={stat.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* About - The Sanctuary */}
      {mockData.sectionVisibility.about && (
      <section id="about" className="py-40 px-6 container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
              <div className="order-2 lg:order-1 relative">
                  <div className="absolute inset-0 bg-[var(--color-primary)]/10 rounded-[var(--radius)] -rotate-3 scale-105" />
                  <VisualImageEditable 
                      src={mockData.about.sectionImage} 
                      className="relative z-10 w-full rounded-[var(--radius)] shadow-xl"
                      onImageChange={(u) => handleUpdate('about.sectionImage', u)}
                      isEditMode={!!isEditMode}
                  />
                  <div className="absolute bottom-8 right-8 z-20 bg-white p-6 rounded-2xl shadow-2xl flex items-center gap-4 animate-float">
                      <div className="bg-[var(--color-accent)] text-white p-3 rounded-full"><Sun size={24}/></div>
                      <div className="text-left"><p className="text-[10px] font-bold uppercase opacity-50">Energy</p><p className="text-sm font-bold">100% Green</p></div>
                  </div>
              </div>
              <div className="order-1 lg:order-2 space-y-10">
                  <div className="inline-block p-4 bg-[var(--color-primary)]/10 rounded-2xl text-[var(--color-primary)]">
                       <TreePine size={32} />
                  </div>
                  <h2 className="text-5xl md:text-6xl font-bold font-[var(--font-heading)] text-[var(--color-text)] leading-tight">
                    <VisualTextEditable value={mockData.about.title} onSave={(v) => handleUpdate('about.title', v)} isEditMode={!!isEditMode} />
                  </h2>
                  <p className="text-xl text-[var(--color-primary)]/70 leading-relaxed font-light italic border-l-4 border-[var(--color-accent)] pl-10">
                    <VisualTextEditable value={mockData.about.description} onSave={(v) => handleUpdate('about.description', v)} isEditMode={!!isEditMode} multiline />
                  </p>
                  <Button className="bg-[var(--color-text)] text-white rounded-full px-12 h-16 text-sm font-bold hover:bg-[var(--color-primary)] shadow-xl transition-all">Explore our Philosophy</Button>
              </div>
          </div>
      </section>
      )}

      {/* Features - Organic Grid */}
      {mockData.sectionVisibility.features && (
      <section className="py-40 bg-[#f0f4ef] px-6">
          <div className="container mx-auto">
              <div className="text-center mb-24 space-y-4">
                  <span className="text-[var(--color-accent)] font-bold tracking-[0.4em] uppercase text-xs">A Balanced Curriculum</span>
                  <h2 className="text-5xl md:text-6xl font-bold font-[var(--font-heading)]">Earth's Classroom.</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  {mockData.features.map((item, i) => (
                      <div key={i} className="group bg-white p-12 rounded-[var(--radius)] shadow-md hover:shadow-2xl hover:-translate-y-4 transition-all duration-500 relative">
                          <div className="w-16 h-16 bg-[var(--color-background)] text-[var(--color-primary)] rounded-2xl flex items-center justify-center mb-8 group-hover:bg-[var(--color-primary)] group-hover:text-white transition-colors duration-500">
                              {React.createElement(item.icon, { size: 32 })}
                          </div>
                          <h3 className="text-2xl font-bold mb-4 font-[var(--font-heading)]">
                              <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-[var(--color-primary)]/70 leading-relaxed font-light">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="text-red-500 font-bold uppercase text-[10px] absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">Discard</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('features', { title: "New Feature", desc: "Brief description of the school feature.", icon: "Leaf" })} className="p-12 border-4 border-dashed border-[var(--color-primary)]/20 rounded-[var(--radius)] flex flex-col items-center justify-center text-[var(--color-primary)]/30 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-all cursor-pointer">
                          <Plus size={64} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Programs - Flourish Paths */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40 px-6">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 mb-32 items-center">
                  <div>
                      <h2 className="text-6xl font-bold font-[var(--font-heading)] text-[var(--color-text)] mb-8">Growth Paths.</h2>
                      <p className="text-lg text-[var(--color-primary)]/70 max-w-xl">Our learning journeys are modular, adaptable, and deeply anchored in environmental awareness.</p>
                  </div>
                   <div className="flex justify-start lg:justify-end gap-10 border-t md:border-t-0 md:border-l border-[var(--color-primary)]/10 pt-12 md:pt-0 md:pl-12">
                       <div><p className="text-3xl font-bold font-[var(--font-heading)]">25+</p><p className="text-xs uppercase font-bold tracking-widest opacity-50">Paths</p></div>
                       <div><p className="text-3xl font-bold font(--font-heading)">1:6</p><p className="text-xs uppercase font-bold tracking-widest opacity-50">Ratio</p></div>
                   </div>
              </div>
              
              <div className="space-y-32">
                  {mockData.programs.map((prog, i) => (
                      <div key={i} className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-16 lg:gap-32 items-center group`}>
                          <div className="w-full lg:w-1/2 relative">
                               <div className="absolute inset-0 bg-[var(--color-secondary)]/10 rounded-full scale-110 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                               <VisualImageEditable 
                                  src={prog.img} 
                                  alt={prog.title}
                                  className="relative z-10 w-full aspect-[4/3] object-cover rounded-[var(--radius)]" 
                                  onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                               <div className={`absolute top-1/2 ${i % 2 === 0 ? '-right-12' : '-left-12'} -translate-y-1/2 w-40 h-40 bg-[var(--color-accent)] z-20 rounded-full flex items-center justify-center p-8 shadow-2xl scale-0 group-hover:scale-100 transition-transform duration-700 hidden lg:flex`}>
                                   <div className="text-center"><p className="text-[10px] font-bold uppercase text-white/80">Focus</p><p className="text-sm font-bold text-white">{prog.age}</p></div>
                               </div>
                          </div>
                          <div className="w-full lg:w-1/2 space-y-6">
                              <span className="text-[var(--color-primary)] font-bold font-mono tracking-widest">PATHWAY_0{i+1}</span>
                              <h3 className="text-5xl font-bold font-[var(--font-heading)] text-[var(--color-text)]">
                                  <VisualTextEditable value={prog.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-xl text-[var(--color-primary)]/70 leading-relaxed font-light">
                                  <VisualTextEditable value={prog.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                              </p>
                              <div className="pt-6">
                                  <button className="flex items-center gap-4 text-sm font-bold uppercase tracking-widest hover:text-[var(--color-accent)] transition-colors group">
                                      Download Syllabus <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:bg-[var(--color-accent)] group-hover:text-white transition-all"><ArrowRight size={20}/></div>
                                  </button>
                              </div>
                              {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="text-red-500 font-bold uppercase text-[10px] mt-8">Remove Pathway</button>}
                          </div>
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('programs', { title: "New Learning Path", age: "Custom Age", desc: "Long description of the new pathway.", img: "" })} className="w-full h-80 border-4 border-dashed border-[var(--color-primary)]/10 rounded-[var(--radius)] flex flex-col items-center justify-center text-[var(--color-primary)]/20 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-all cursor-pointer">
                          <Plus size={48} className="mb-4" />
                          <span className="uppercase font-bold tracking-widest text-xs">Create Growth Path</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Facilities - The Campus Village */}
      {mockData.sectionVisibility.facilities && (
      <section id="facilities" className="py-40 bg-[var(--color-text)] text-white px-6">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-32">
                  <div>
                      <span className="text-[var(--color-accent)] font-bold tracking-[0.4em] uppercase text-xs mb-4 block">Physical Context</span>
                      <h2 className="text-6xl font-bold font-[var(--font-heading)] italic">Our Village.</h2>
                  </div>
                  <p className="text-white/50 max-w-sm mb-4">Every structure on our campus is designed to be a teaching tool, built with local materials and sustainable techniques.</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                  {mockData.facilities.map((fac, i) => (
                      <div key={i} className="group relative">
                          <div className="relative aspect-[16/10] overflow-hidden rounded-[var(--radius)] mb-8">
                              <VisualImageEditable 
                                  src={fac.img} alt={fac.title}
                                  className="w-full h-full object-cover grayscale opacity-40 group-hover:scale-105 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-1000"
                                  onImageChange={(u) => handleUpdate(`facilities[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute top-6 left-6 p-4 bg-[var(--color-accent)] text-white rounded-2xl shadow-xl">
                                  {React.createElement(getIcon(fac.icon), { size: 28 })}
                              </div>
                          </div>
                          <div className="space-y-4">
                              <h3 className="text-4xl font-bold font-[var(--font-heading)]">
                                  <VisualTextEditable value={fac.title} onSave={(v) => handleUpdate(`facilities[${i}].title`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-white/60 font-light leading-relaxed max-w-lg">
                                  <VisualTextEditable value={fac.desc} onSave={(v) => handleUpdate(`facilities[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('facilities', i)} className="text-red-400 font-bold uppercase text-[10px] mt-6 hover:text-red-600">Delete Structure</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('facilities', { title: "New Learning Space", desc: "Detailed description of space.", img: "", icon: "Zap" })} className="w-full h-80 border-4 border-dashed border-white/10 rounded-[var(--radius)] flex flex-col items-center justify-center text-white/10 hover:text-white transition-all cursor-pointer">
                          <Plus size={48} />
                          <span className="uppercase font-bold tracking-widest text-xs mt-4">Append Structure</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Gallery - Natural Perspective */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="py-40 px-6 container mx-auto">
          <div className="text-center mb-24">
              <span className="text-[var(--color-primary)] font-bold tracking-[0.8em] uppercase text-xs mb-8 block">MOMENTS IN THE WILD</span>
              <h2 className="text-6xl md:text-8xl font-bold font-[var(--font-heading)] text-[var(--color-text)]">Life_Gallery.</h2>
          </div>
          
          <div className="columns-1 md:columns-2 lg:columns-3 gap-10 space-y-10">
              {mockData.gallery.map((item, i) => (
                  <div key={i} className="break-inside-avoid relative group rounded-[var(--radius)] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-700">
                      <VisualImageEditable 
                          src={item.img} alt={item.title}
                          className="w-full object-cover group-hover:scale-105 transition-transform duration-1000"
                          onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                          isEditMode={!!isEditMode}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-text)]/80 via-transparent to-transparent p-10 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-xs font-bold text-[var(--color-accent)] uppercase mb-2 italic">#{item.category}</p>
                          <h4 className="text-2xl font-bold text-white font-[var(--font-heading)]">{item.title}</h4>
                      </div>
                  </div>
              ))}
              {isEditMode && (
                  <div onClick={() => addArrayItem('gallery', { category: "Campus", title: "New Discovery", img: "" })} className="w-full aspect-square border-4 border-dashed border-[var(--color-primary)]/10 rounded-[var(--radius)] flex flex-col items-center justify-center text-[var(--color-primary)]/10 hover:text-[var(--color-primary)] transition-all cursor-pointer">
                      <Camera size={48} />
                  </div>
              )}
          </div>
      </section>
      )}

      {/* Team - Seed Bearers */}
      {mockData.sectionVisibility.team && (
      <section id="team" className="py-40 bg-[#f9faf7] px-6">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-32 items-end border-b border-[var(--color-primary)]/10 pb-12">
                  <div className="lg:col-span-8">
                      <h2 className="text-6xl font-bold font-[var(--font-heading)] text-[var(--color-text)] leading-none italic">The Seed Bearers.</h2>
                  </div>
                   <div className="lg:col-span-4 text-right">
                       <p className="text-[var(--color-primary)]/60 italic">Mentors who guide our students with patience and presence.</p>
                   </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                  {mockData.team.map((m, i) => (
                      <div key={i} className="group relative flex flex-col items-center text-center space-y-8">
                          <div className="relative overflow-hidden aspect-square w-full rounded-full border-8 border-white shadow-xl hover:shadow-[0_20px_50px_rgba(93,135,95,0.2)] transition-all duration-700">
                               <VisualImageEditable 
                                  src={m.img} 
                                  alt={m.name} 
                                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-1000" 
                                  onImageChange={(u) => handleUpdate(`team[${i}].img`, u)} 
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute top-4 right-4 p-4 bg-[var(--color-accent)] text-white rounded-full z-20 scale-0 group-hover:scale-100 transition-transform">
                                   <Zap size={16} />
                               </div>
                          </div>
                          <div>
                              <h3 className="text-3xl font-bold font-[var(--font-heading)] text-[var(--color-text)] mb-2">
                                  <VisualTextEditable value={m.name} onSave={(v) => handleUpdate(`team[${i}].name`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--color-accent)]">
                                  <VisualTextEditable value={m.role} onSave={(v) => handleUpdate(`team[${i}].role`, v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                           {isEditMode && <button onClick={() => removeArrayItem('team', i)} className="text-red-500 font-bold uppercase text-[9px] absolute top-[-30px]">Relocate Educator</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('team', { name: "New Guide", role: "Specialist", img: "" })} className="aspect-square w-full border-4 border-dashed border-[var(--color-primary)]/10 rounded-full flex flex-col items-center justify-center text-[var(--color-primary)]/20 hover:text-[var(--color-primary)] transition-all cursor-pointer">
                          <Plus size={48} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* News - Seasonal Dispatch */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 px-6">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-end mb-32 border-b border-[var(--color-primary)]/10 pb-12 gap-8">
                   <div>
                       <span className="text-[var(--color-primary)] font-bold tracking-[0.4em] uppercase text-xs mb-4 block">SEASONAL DISPATCH</span>
                       <h2 className="text-6xl font-bold font-[var(--font-heading)] text-[var(--color-text)]">Wild_Logs.</h2>
                   </div>
                   <button className="text-sm font-bold uppercase tracking-widest text-[var(--color-accent)] hover:underline">See Archived Logs</button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="group flex flex-col rounded-[var(--radius)] overflow-hidden transition-all duration-700">
                          <div className="relative aspect-video overflow-hidden rounded-[var(--radius)] mb-10 shadow-lg">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md px-6 py-3 rounded-full text-[10px] font-bold text-[var(--color-text)] uppercase tracking-widest">
                                  {item.date}
                              </div>
                          </div>
                          <h3 className="text-3xl font-bold font-[var(--font-heading)] text-[var(--color-text)] mb-6 group-hover:text-[var(--color-primary)] transition-colors">
                              <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-lg text-[var(--color-primary)]/70 font-light leading-relaxed mb-8 flex-1">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-[var(--color-text)]">
                              Read Log <div className="w-10 h-[2px] bg-[var(--color-accent)] group-hover:w-20 transition-all duration-500" />
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads - Resource Basket */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-40 bg-[#f9faf7] px-6">
          <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-24 space-y-8">
                  <div className="w-20 h-20 bg-white shadow-xl rounded-2xl flex items-center justify-center mx-auto text-[var(--color-primary)]">
                       <FileText size={40} />
                  </div>
                  <h2 className="text-5xl font-bold font-[var(--font-heading)]">
                      <VisualTextEditable 
                          value={mockData.labels.downloadsTitle} 
                          onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                          isEditMode={!!isEditMode} 
                          as="span" 
                      />
                  </h2>
                  <p className="text-lg text-[var(--color-primary)]/60 italic font-light">
                      <VisualTextEditable 
                          value={mockData.labels.downloadsSection} 
                          onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                          isEditMode={!!isEditMode} 
                          as="span" 
                      />
                  </p>
              </div>
              
              <div className="space-y-6">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="group bg-white p-10 rounded-[var(--radius)] flex flex-col md:flex-row justify-between items-start md:items-center shadow-sm hover:shadow-2xl hover:scale-[1.02] transition-all duration-500 cursor-pointer">
                          <div className="flex items-center gap-10">
                              <div className="p-6 bg-[var(--color-background)] text-[var(--color-primary)] rounded-full group-hover:bg-[var(--color-primary)] group-hover:text-white transition-colors">
                                  <Download size={32} />
                              </div>
                              <div>
                                  <h4 className="text-2xl font-bold font-[var(--font-heading)] mb-2 group-hover:text-[var(--color-primary)] transition-colors">
                                      <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                                  </h4>
                                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">
                                      <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" /> // 
                                      <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" />
                                  </p>
                              </div>
                          </div>
                          <div className="flex gap-10 items-center mt-10 md:mt-0">
                              <button className="bg-[var(--color-text)] text-white px-8 py-4 rounded-full text-[10px] font-bold uppercase tracking-widest">Download</button>
                              {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="text-red-500 font-bold uppercase text-[9px]">Discard</button>}
                          </div>
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('downloads', { title: "New Documentation", size: "1.0 MB", type: "PDF" })} className="p-10 border-4 border-dashed border-[var(--color-primary)]/10 rounded-[var(--radius)] flex flex-col items-center justify-center text-[var(--color-primary)]/20 hover:text-[var(--color-primary)] transition-all cursor-pointer">
                          <Plus size={48} />
                          <span className="uppercase font-bold tracking-widest text-[10px] mt-4">Append Resource</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* CTA - The Final Call */}
      {mockData.sectionVisibility.cta && (
      <section id="cta" className="py-64 relative bg-[var(--color-primary)] overflow-hidden text-center px-6">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/leaf.png')] opacity-10" />
          <div className="container mx-auto relative z-10 max-w-4xl text-white">
              <h2 className="text-7xl md:text-9xl font-bold font-[var(--font-heading)] italic leading-none mb-16">
                 Bloom <br/> with Us.
              </h2>
              <p className="text-2xl md:text-4xl font-light text-white/80 mb-24 italic max-w-3xl mx-auto leading-tight">
                Growth is not a race. It's a journey of connection, understanding, and presence.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-10">
                   <Button className="bg-[var(--color-accent)] text-[var(--color-text)] rounded-full h-24 px-16 font-black uppercase tracking-[0.2em] text-sm hover:scale-110 shadow-3x; transition-all">
                       Initialize Growth Path
                   </Button>
                   <Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[var(--color-text)] rounded-full h-24 px-16 font-black uppercase tracking-[0.2em] text-sm transition-all">
                       Visit the Sanctuary
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - Organic Ground */}
      <footer id="contact" className="bg-[#f0f4ef] text-[var(--color-text)] pt-40 pb-20 px-6 border-t border-[var(--color-primary)]/10">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-40 items-start">
                  <div className="lg:col-span-5">
                      <div className="flex items-center gap-4 mb-12">
                           <Leaf size={40} className="text-[var(--color-primary)]" />
                           <div className="text-4xl font-bold font-[var(--font-heading)] text-[var(--color-text)] italic leading-none">{schoolName}.</div>
                      </div>
                      <p className="text-xl font-light leading-relaxed mb-16 italic text-[var(--color-primary)]/60 border-l-4 border-[var(--color-accent)] pl-10 max-w-md">"The tree that grows the tallest has the deepest roots. We help our students find theirs."</p>
                      <div className="flex gap-4">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white shadow-sm transition-all"><Icon size={24} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[10px] font-bold tracking-[0.6em] uppercase text-[var(--color-primary)] mb-12 opacity-40">NAVIGATION</h4>
                      <ul className="space-y-6 text-sm font-semibold">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:text-[var(--color-accent)] transition-all flex items-center gap-4">[{i+1}] {item.label}</a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-3">
                      <h4 className="text-[10px] font-bold tracking-[0.6em] uppercase text-[var(--color-primary)] mb-12 opacity-40">COMMUNICATIONS</h4>
                      <div className="space-y-12">
                          <div className="flex gap-8 group">
                              <MapPin size={32} className="text-[var(--color-accent)] shrink-0" />
                              <p className="text-sm font-semibold leading-relaxed">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="flex gap-8 group">
                              <Phone size={32} className="text-[var(--color-primary)] shrink-0" />
                              <p className="text-sm font-semibold">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>

                  <div className="lg:col-span-2">
                      <h4 className="text-[10px] font-bold tracking-[0.6em] uppercase text-[var(--color-primary)] mb-12 opacity-40">STAY CONNECTED</h4>
                      <div className="space-y-8">
                           <p className="text-xs text-[var(--color-primary)]/60 font-medium italic leading-relaxed">Join our mailing community for forest updates.</p>
                           <div className="flex gap-2 border-b-2 border-[var(--color-primary)]/20 pb-4 hover:border-[var(--color-accent)] transition-all">
                                <input type="email" placeholder="YOUR@EMAIL.COM" className="bg-transparent border-none focus:ring-0 text-sm font-medium w-full placeholder:text-gray-400" />
                                <button className="text-[var(--color-accent)] hover:text-[var(--color-text)] transition-colors"><Send size={24} /></button>
                           </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-24 border-t border-[var(--color-primary)]/10 flex flex-col md:flex-row justify-between items-center gap-12 text-[10px] font-bold tracking-[0.4em] uppercase text-[var(--color-primary)] opacity-40">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // ROOTED IN COMMUNITY // ALL SECURE</p>
                  <div className="flex gap-16 underline underline-offset-8">
                      <a href="#" className="hover:text-[var(--color-text)]">ETHICS CHARTER</a>
                      <a href="#" className="hover:text-[var(--color-text)]">GDPR_VALLEY</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* Mobile Menu Overlay - Organic Fade */}
      {menuOpen && (
          <div className="fixed inset-0 z-[100] bg-white p-12 flex flex-col justify-between animate-in fade-in slide-in-from-top duration-500">
              <div className="flex justify-between items-center">
                  <div className="text-3xl font-bold font-[var(--font-heading)] italic text-[var(--color-text)]">{schoolName}</div>
                  <button onClick={() => setMenuOpen(false)} className="text-[var(--color-primary)]"><X size={32} /></button>
              </div>
              <nav className="flex flex-col gap-12">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-5xl font-bold font-[var(--font-heading)] italic text-[var(--color-text)] hover:text-[var(--color-accent)] transition-all">
                          {n.label}
                      </a>
                  ))}
              </nav>
              <div className="pt-20 border-t border-[var(--color-primary)]/10">
                  <Button className="w-full bg-[var(--color-text)] text-white rounded-full h-24 font-bold text-sm">Initialize Growth Path</Button>
              </div>
          </div>
      )}
    </div>
  );
};

export default NatureCampus;
